import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSupabaseRpc } from '../../hooks/useSupabase'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, ComposedChart, Area, Line
} from 'recharts'

const DEVICE_COLORS = ['#3B82F6', '#8B5CF6', '#6B7280']

const DARK_TOOLTIP_STYLE = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#fff'
}

// Viewer's IANA timezone, used to bucket the hourly chart server-side so
// "hour of day" matches the user's local clock regardless of where the
// short_url_click_events rows were written from.
const BROWSER_TZ =
  (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC'

// Country display name — uses Intl.DisplayNames (browser standard, zero deps).
// Falls back to the ISO code itself on any error.
const COUNTRY_NAME_FMT = (() => {
  try {
    return typeof Intl !== 'undefined' && Intl.DisplayNames
      ? new Intl.DisplayNames(['en'], { type: 'region' })
      : null
  } catch {
    return null
  }
})()
function countryLabel(code) {
  if (!code) return 'Unknown'
  try {
    return COUNTRY_NAME_FMT?.of(code) || code
  } catch {
    return code
  }
}
// Two-letter ISO code → regional indicator emoji pair. Works for any
// well-formed alpha-2; invalid inputs return an empty string.
function countryFlag(code) {
  if (!code || code.length !== 2) return ''
  const A = 0x41 // 'A'
  const offset = 0x1F1E6 // 🇦
  const cc = code.toUpperCase()
  return String.fromCodePoint(cc.charCodeAt(0) - A + offset, cc.charCodeAt(1) - A + offset)
}

function truncateUrl(url, maxLen = 50) {
  if (!url) return ''
  return url.length > maxLen ? url.slice(0, maxLen) + '...' : url
}

function formatTimestamp(ts) {
  const d = new Date(ts)
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

// "in 2d 4h" style countdown for the Expiring Soon panel.
function countdownTo(iso) {
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return 'now'
  const d = Math.floor(ms / 86400000)
  const h = Math.floor((ms % 86400000) / 3600000)
  if (d > 0) return `in ${d}d ${h}h`
  if (h > 0) return `in ${h}h`
  const m = Math.floor((ms % 3600000) / 60000)
  return `in ${m}m`
}

// Period-over-period delta pill. `kind='percent'` for ratio-based KPIs,
// `kind='absolute'` for counters where we only care about the new additions.
function DeltaBadge({ current, previous, kind = 'percent' }) {
  if (kind === 'absolute') {
    if (!current) return null
    return (
      <span className="ml-2 text-xs font-medium text-green-600 dark:text-green-400">
        +{current} this period
      </span>
    )
  }
  if (previous === 0 && current === 0) return null
  if (previous === 0) {
    return (
      <span className="ml-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        new
      </span>
    )
  }
  const pct = Math.round(((current - previous) / previous) * 100)
  const positive = pct >= 0
  const cls = positive
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400'
  return (
    <span className={`ml-2 text-xs font-medium ${cls}`}>
      {positive ? '+' : ''}{pct}% vs prev
    </span>
  )
}

// [p_from, p_to) for a given UI dateRange. Uses local midnight for "today"
// so the window matches the viewer's calendar day.
function rangeFor(dateRange) {
  const to = new Date()
  if (dateRange === 'all') {
    return { from: null, to: to.toISOString() }
  }
  const from = new Date()
  if (dateRange === 'today') from.setHours(0, 0, 0, 0)
  else if (dateRange === '7d') from.setDate(to.getDate() - 7)
  else if (dateRange === '30d') from.setDate(to.getDate() - 30)
  return { from: from.toISOString(), to: to.toISOString() }
}

export default function DashboardTab({ selectedAccount }) {
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useState('30d')
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [deviceBrowserOpen, setDeviceBrowserOpen] = useState(false)
  const [hourlyOpen, setHourlyOpen] = useState(false)
  const [expiringOpen, setExpiringOpen] = useState(true)
  const [coverageDrawerOpen, setCoverageDrawerOpen] = useState(false)
  const [chartMode, setChartMode] = useState('both') // 'clicks' | 'urls' | 'both'
  const [refreshing, setRefreshing] = useState(false)
  const [excludeBots, setExcludeBots] = useState(true)
  const [recentSearch, setRecentSearch] = useState('')
  const [exporting, setExporting] = useState(false)
  const [geoOpen, setGeoOpen] = useState(false)

  const { from, to } = useMemo(() => rangeFor(dateRange), [dateRange])
  const accountId = selectedAccount?.id ?? null
  const enabled = accountId != null

  const summaryRpc = useSupabaseRpc(
    'shortlink_dashboard_summary',
    { p_account_id: accountId, p_from: from, p_to: to, p_tz: BROWSER_TZ, p_exclude_bots: excludeBots },
    { enabled }
  )
  const daySeriesRpc = useSupabaseRpc(
    'shortlink_dashboard_series',
    { p_account_id: accountId, p_from: from, p_to: to, p_bucket: 'day', p_tz: BROWSER_TZ, p_exclude_bots: excludeBots },
    { enabled }
  )
  const hourSeriesRpc = useSupabaseRpc(
    'shortlink_dashboard_series',
    { p_account_id: accountId, p_from: from, p_to: to, p_bucket: 'hour', p_tz: BROWSER_TZ, p_exclude_bots: excludeBots },
    { enabled }
  )
  const distributionsRpc = useSupabaseRpc(
    'shortlink_dashboard_distributions',
    { p_account_id: accountId, p_from: from, p_to: to, p_exclude_bots: excludeBots },
    { enabled }
  )
  const topUrlsRpc = useSupabaseRpc(
    'shortlink_dashboard_top_urls',
    { p_account_id: accountId, p_from: from, p_to: to, p_limit: 10, p_exclude_bots: excludeBots },
    { enabled }
  )
  const recentClicksRpc = useSupabaseRpc(
    'shortlink_dashboard_recent_clicks',
    { p_account_id: accountId, p_limit: 20, p_exclude_bots: excludeBots },
    { enabled }
  )
  const urlsCreatedSeriesRpc = useSupabaseRpc(
    'shortlink_dashboard_urls_created_series',
    { p_account_id: accountId, p_from: from, p_to: to, p_bucket: 'day', p_tz: BROWSER_TZ },
    { enabled }
  )
  const expiringSoonRpc = useSupabaseRpc(
    'shortlink_dashboard_expiring_soon',
    { p_account_id: accountId, p_window: '7 days' },
    { enabled }
  )
  const coverageRpc = useSupabaseRpc(
    'shortlink_dashboard_expiry_url_coverage',
    { p_account_id: accountId },
    { enabled }
  )
  const byCountryRpc = useSupabaseRpc(
    'shortlink_dashboard_by_country',
    { p_account_id: accountId, p_from: from, p_to: to, p_limit: 10, p_exclude_bots: excludeBots },
    { enabled }
  )

  const loading = enabled && (
    summaryRpc.loading || daySeriesRpc.loading || hourSeriesRpc.loading ||
    distributionsRpc.loading || topUrlsRpc.loading || recentClicksRpc.loading ||
    urlsCreatedSeriesRpc.loading || expiringSoonRpc.loading || coverageRpc.loading ||
    byCountryRpc.loading
  )

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await Promise.all([
        summaryRpc.refetch(),
        daySeriesRpc.refetch(),
        hourSeriesRpc.refetch(),
        distributionsRpc.refetch(),
        topUrlsRpc.refetch(),
        recentClicksRpc.refetch(),
        urlsCreatedSeriesRpc.refetch(),
        expiringSoonRpc.refetch(),
        coverageRpc.refetch(),
        byCountryRpc.refetch(),
      ])
    } finally {
      setRefreshing(false)
    }
  }

  // Derive the single summary row. RPCs return arrays even for single-row results.
  const summary = Array.isArray(summaryRpc.data) && summaryRpc.data.length > 0
    ? summaryRpc.data[0]
    : null
  const totalUrls = Number(summary?.total_urls ?? 0)
  const activeUrls = Number(summary?.active_urls ?? 0)
  const expiredUrls = Number(summary?.expired_urls ?? 0)
  const totalClicks = Number(summary?.total_clicks ?? 0)
  const uniqueReferrers = Number(summary?.unique_referrers ?? 0)
  const avgClicksPerUrl = activeUrls > 0 ? (totalClicks / activeUrls).toFixed(1) : '0.0'
  const topBrowserName = summary?.top_browser || null
  const topBrowserShare = Number(summary?.top_browser_share ?? 0)
  const topBrowserRunnerup = summary?.top_browser_runnerup || null
  const topBrowserRunnerupShare = Number(summary?.top_browser_runnerup_share ?? 0)
  const topOSName = summary?.top_os || null
  const topOSShare = Number(summary?.top_os_share ?? 0)
  const topOSRunnerup = summary?.top_os_runnerup || null
  const topOSRunnerupShare = Number(summary?.top_os_runnerup_share ?? 0)
  const topBrowser = topBrowserName ? `${topBrowserName} ${topBrowserShare}%` : 'N/A'
  const topOS = topOSName ? `${topOSName} ${topOSShare}%` : 'N/A'
  const topBrowserSub = topBrowserRunnerup
    ? `${topBrowserRunnerup} ${topBrowserRunnerupShare}%`
    : 'Most common'
  const topOSSub = topOSRunnerup
    ? `${topOSRunnerup} ${topOSRunnerupShare}%`
    : 'Most common'
  // Top Country (Phase 4): formatted as "🇿🇦 South Africa 45%".
  const topCountryCode = summary?.top_country || null
  const topCountryShare = Number(summary?.top_country_share ?? 0)
  const topCountryRunnerupCode = summary?.top_country_runnerup || null
  const topCountryRunnerupShare = Number(summary?.top_country_runnerup_share ?? 0)
  const topCountry = topCountryCode
    ? `${countryFlag(topCountryCode)} ${countryLabel(topCountryCode)} ${topCountryShare}%`
    : 'N/A'
  const topCountrySub = topCountryRunnerupCode
    ? `${countryFlag(topCountryRunnerupCode)} ${countryLabel(topCountryRunnerupCode)} ${topCountryRunnerupShare}%`
    : 'Most common'
  // Phase 3 period-over-period fields
  const urlsCreatedCurrent = Number(summary?.urls_created_current ?? 0)
  const urlsCreatedPrev = Number(summary?.urls_created_prev ?? 0)
  const clicksPrev = Number(summary?.clicks_prev_period ?? 0)
  const uniqueReferrersPrev = Number(summary?.unique_referrers_prev ?? 0)

  // Merged click + urls-created time series, keyed by day. Used by the
  // volume chart; the toggle below decides which series render.
  const chartData = useMemo(() => {
    const map = new Map()
    ;(daySeriesRpc.data || []).forEach(r => {
      const d = new Date(r.bucket_start).toISOString().slice(0, 10)
      map.set(d, { date: d, clicks: Number(r.clicks) || 0, urls_created: 0 })
    })
    ;(urlsCreatedSeriesRpc.data || []).forEach(r => {
      const d = new Date(r.bucket_start).toISOString().slice(0, 10)
      const prev = map.get(d) || { date: d, clicks: 0, urls_created: 0 }
      prev.urls_created = Number(r.urls_created) || 0
      map.set(d, prev)
    })
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [daySeriesRpc.data, urlsCreatedSeriesRpc.data])

  const hourlyData = useMemo(() => {
    const base = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}:00`, clicks: 0 }))
    ;(hourSeriesRpc.data || []).forEach(r => {
      const h = new Date(r.bucket_start).getHours()
      if (h >= 0 && h < 24) base[h].clicks += Number(r.clicks) || 0
    })
    return base
  }, [hourSeriesRpc.data])

  const deviceData = useMemo(
    () => (distributionsRpc.data || [])
      .filter(r => r.dimension === 'device')
      .map(r => ({ name: r.bucket, value: Number(r.clicks) || 0 }))
      .filter(d => d.value > 0),
    [distributionsRpc.data]
  )

  const browserData = useMemo(
    () => (distributionsRpc.data || [])
      .filter(r => r.dimension === 'browser')
      .map(r => ({ name: r.bucket, clicks: Number(r.clicks) || 0 })),
    [distributionsRpc.data]
  )

  const topUrls = useMemo(
    () => (topUrlsRpc.data || []).map(r => ({
      url: {
        id: Number(r.short_url_id),
        slug: r.slug,
        original_url: r.original_url,
      },
      clicks: Number(r.clicks) || 0,
    })),
    [topUrlsRpc.data]
  )
  const maxClicks = topUrls.length > 0 ? topUrls[0].clicks : 1

  const recentClicks = useMemo(
    () => (recentClicksRpc.data || []).map(r => ({
      id: r.id,
      timestamp: r.click_timestamp,
      slug: r.slug,
      device_type: r.device_type,
      client_name: r.client_name,
      referrer: r.referrer,
    })),
    [recentClicksRpc.data]
  )

  const byCountry = useMemo(
    () => (byCountryRpc.data || []).map(r => ({
      code: r.country_code,
      name: countryLabel(r.country_code),
      flag: countryFlag(r.country_code),
      clicks: Number(r.clicks) || 0,
      share: Number(r.share) || 0,
    })),
    [byCountryRpc.data]
  )

  // Phase 3: expiring-soon list and expiry_url coverage chip.
  const expiring = expiringSoonRpc.data || []
  const coverage = Array.isArray(coverageRpc.data) && coverageRpc.data.length > 0
    ? coverageRpc.data[0]
    : null
  const urlsWithoutExpiry = useMemo(
    () => expiring.filter(r => !r.has_expiry_url),
    [expiring]
  )

  // Account selection is required — the RPCs key on p_account_id.
  if (!enabled) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-gray-500 dark:text-gray-400 text-lg">No account selected</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Pick a shortlink account from the Accounts tab to see its dashboard.</p>
      </div>
    )
  }

  // Client-side filter for Recent Activity search (gap #18).
  const filteredRecentClicks = useMemo(() => {
    const q = recentSearch.trim().toLowerCase()
    if (!q) return recentClicks
    return recentClicks.filter(c =>
      (c.slug && c.slug.toLowerCase().includes(q)) ||
      (c.device_type && c.device_type.toLowerCase().includes(q)) ||
      (c.client_name && c.client_name.toLowerCase().includes(q)) ||
      (c.referrer && c.referrer.toLowerCase().includes(q))
    )
  }, [recentClicks, recentSearch])

  // Inline skeleton pulser, co-located per repo style.
  const Skeleton = ({ className = '' }) => (
    <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
  )

  // CSV export (gap #12). Serializes rows via RFC4180-ish quoting; no new deps.
  const toCsv = (rows, headers) => {
    if (!rows || rows.length === 0) return headers.join(',') + '\n'
    const esc = (v) => {
      if (v === null || v === undefined) return ''
      const s = String(v)
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const head = headers.join(',')
    const body = rows.map(r => headers.map(h => esc(r[h])).join(',')).join('\n')
    return head + '\n' + body + '\n'
  }

  const handleExportCsv = async () => {
    setExporting(true)
    try {
      const { supabase } = await import('../../lib/supabase')
      const { data, error } = await supabase.rpc('shortlink_dashboard_export', {
        p_account_id: accountId,
        p_from: from,
        p_to: to,
        p_exclude_bots: excludeBots,
      })
      if (error) throw error
      const headers = [
        'click_timestamp', 'slug', 'original_url', 'referrer',
        'device_type', 'client_name', 'client_version',
        'operating_system_name', 'user_agent'
      ]
      const csv = toCsv(data || [], headers)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const slugPart = (selectedAccount.name || 'account').toLowerCase().replace(/[^a-z0-9]+/g, '-')
      const fromPart = from ? from.slice(0, 10) : 'all'
      const toPart = to ? to.slice(0, 10) : 'now'
      a.href = url
      a.download = `shortlink-clicks-${slugPart}-${fromPart}_${toPart}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('CSV export failed:', err)
      alert(`Export failed: ${err.message || err}`)
    } finally {
      setExporting(false)
    }
  }

  // Empty-state trap fix (#5): only block the whole dashboard if there are
  // no URLs AND no clicks. Orphaned click rows still render so they can be
  // investigated. Skip this check while summary is still loading so we
  // don't flash the empty state before data arrives.
  if (!summaryRpc.loading && totalUrls === 0 && totalClicks === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.193-9.193a4.5 4.5 0 016.364 6.364l-4.5 4.5a4.5 4.5 0 01-7.244-1.242" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">No data yet</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Create short URLs to see analytics here.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh + Date Range Filter + Bot toggle + Export */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Shortlink Service Dashboard</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Filtered by: {selectedAccount.name}</p>
          </div>
          <div className="flex items-center flex-wrap gap-3">
            <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {[
                { value: 'today', label: 'Today' },
                { value: '7d', label: '7 Days' },
                { value: '30d', label: '30 Days' },
                { value: 'all', label: 'All Time' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDateRange(opt.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                    dateRange === opt.value
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >{opt.label}</button>
              ))}
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {refreshing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
            </button>
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download clicks for the selected range as CSV (up to 50k rows)"
            >
              {exporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              <span>{exporting ? 'Exporting…' : 'Export CSV'}</span>
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end">
          <label className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeBots}
              onChange={(e) => setExcludeBots(e.target.checked)}
              className="w-4 h-4 text-primary-500 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 focus:ring-2"
            />
            <span>Exclude likely bots</span>
            <span className="text-xs text-gray-400 dark:text-gray-500" title="Filters clicks where the user agent looks like curl, wget, a crawler, or a headless browser. Raw data is not modified.">ⓘ</span>
          </label>
        </div>
      </div>

      {/* 8 KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total URLs', value: totalUrls, color: 'blue', sub: null, delta: <DeltaBadge current={urlsCreatedCurrent} previous={urlsCreatedPrev} kind="absolute" />, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.193-9.193a4.5 4.5 0 016.364 6.364l-4.5 4.5a4.5 4.5 0 01-7.244-1.242" /> },
          { label: 'Active URLs', value: activeUrls, color: 'green', sub: totalUrls > 0 ? `${Math.round((activeUrls / totalUrls) * 100)}% of total` : null, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> },
          { label: 'Expired URLs', value: expiredUrls, color: 'amber', sub: totalUrls > 0 ? `${Math.round((expiredUrls / totalUrls) * 100)}% expired` : null, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /> },
          { label: 'Total Clicks', value: totalClicks, color: 'purple', sub: null, delta: <DeltaBadge current={totalClicks} previous={clicksPrev} />, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" /> },
          { label: 'Avg Clicks/URL', value: avgClicksPerUrl, color: 'indigo', sub: 'Per active URL', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /> },
          { label: 'Unique Referrers', value: uniqueReferrers, color: 'teal', sub: 'Distinct sources', delta: <DeltaBadge current={uniqueReferrers} previous={uniqueReferrersPrev} />, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /> },
          { label: 'Top Browser', value: topBrowser, color: 'pink', sub: topBrowserSub, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" /> },
          { label: 'Top OS', value: topOS, color: 'cyan', sub: topOSSub, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" /> },
          { label: 'Top Country', value: topCountry, color: 'orange', sub: topCountrySub, icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /> },
        ].map(({ label, value, color, sub, icon, delta }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-default transition hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
                {summaryRpc.loading ? (
                  <>
                    <Skeleton className="h-9 w-24 mt-2" />
                    <Skeleton className="h-3 w-20 mt-2" />
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2 truncate max-w-[140px]" title={String(value)}>{value}</p>
                      {delta}
                    </div>
                    {sub && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>
                    )}
                  </>
                )}
              </div>
              <div className={`bg-${color}-500 p-3 rounded-lg text-white`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {icon}
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expiring Soon panel (P3 #2, #3) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setExpiringOpen(!expiringOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Expiring in next 7 days
                {expiring.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-semibold rounded">
                    {expiring.length}
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Fallback-URL coverage on active expiring links</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {coverage && coverage.total_expiring > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setCoverageDrawerOpen(true)
                  setExpiringOpen(true)
                }}
                className="px-3 py-1 bg-gray-100 dark:bg-gray-900 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition"
                title="Click to see URLs without a fallback"
              >
                {Number(coverage.with_expiry_url)} / {Number(coverage.total_expiring)} have fallback
              </button>
            )}
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${expiringOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {expiringOpen && (
          <div className="p-6">
            {expiring.length === 0 ? (
              <p className="text-gray-400 dark:text-gray-500 text-sm">No URLs expire in the next 7 days.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-2 pr-2">Slug</th>
                      <th className="pb-2 pr-2">Target</th>
                      <th className="pb-2 pr-2">Expires</th>
                      <th className="pb-2 pr-2">In</th>
                      <th className="pb-2">Fallback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(coverageDrawerOpen ? urlsWithoutExpiry : expiring).map(r => (
                      <tr key={r.id} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className="py-2 pr-2 font-mono text-xs text-indigo-600 dark:text-indigo-400">/{r.slug}</td>
                        <td className="py-2 pr-2 text-gray-700 dark:text-gray-300 text-xs truncate max-w-[260px]" title={r.original_url}>
                          {truncateUrl(r.original_url, 40)}
                        </td>
                        <td className="py-2 pr-2 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">
                          {new Date(r.expiry_datetime_utc).toLocaleString(undefined, {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="py-2 pr-2 text-gray-900 dark:text-white text-xs font-medium whitespace-nowrap">
                          {countdownTo(r.expiry_datetime_utc)}
                        </td>
                        <td className="py-2">
                          {r.has_expiry_url ? (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded">
                              set
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded">
                              none
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {coverageDrawerOpen && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => setCoverageDrawerOpen(false)}
                      className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Show all expiring URLs
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Performance Analytics Accordion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setAnalyticsOpen(!analyticsOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Analytics</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Click trends, device breakdown, and activity insights</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${analyticsOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {analyticsOpen && (
          <div className="p-6 space-y-4">
            {/* Click Volume + URL creation cadence */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">Click Volume & Creation Cadence</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Clicks and new URL creation over the selected date range</p>
                </div>
                <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  {[
                    { value: 'clicks', label: 'Clicks only' },
                    { value: 'urls', label: 'URLs only' },
                    { value: 'both', label: 'Both' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setChartMode(opt.value)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                        chartMode === opt.value
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {chartData.length === 0 ? (
                <p className="text-gray-400 dark:text-gray-500 text-sm">No activity in this period.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <Tooltip contentStyle={DARK_TOOLTIP_STYLE} />
                    <Legend />
                    {(chartMode === 'clicks' || chartMode === 'both') && (
                      <Area type="monotone" dataKey="clicks" name="Clicks" stroke="#6366f1" strokeWidth={2} fill="url(#clicksGradient)" />
                    )}
                    {(chartMode === 'urls' || chartMode === 'both') && (
                      <Line type="monotone" dataKey="urls_created" name="URLs created" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Device & Browser Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button onClick={() => setDeviceBrowserOpen(!deviceBrowserOpen)} className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Device & Browser Breakdown</span>
                </div>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${deviceBrowserOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {deviceBrowserOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Device Breakdown</h3>
                      {deviceData.length === 0 ? (
                        <p className="text-gray-400 dark:text-gray-500 text-sm">No click data yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={deviceData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={4}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {deviceData.map((entry, index) => (
                                <Cell key={entry.name} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Legend />
                            <Tooltip contentStyle={DARK_TOOLTIP_STYLE} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Browsers</h3>
                      {browserData.length === 0 ? (
                        <p className="text-gray-400 dark:text-gray-500 text-sm">No click data yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart layout="vertical" data={browserData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                            <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={DARK_TOOLTIP_STYLE} />
                            <Bar dataKey="clicks" fill="#10B981" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Geography Sub-Accordion (Phase 4 #6) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button onClick={() => setGeoOpen(!geoOpen)} className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-orange-100 dark:bg-orange-900 rounded">
                    <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Geography</span>
                </div>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${geoOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {geoOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                    Top countries by click count. Country is sourced from the <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-900 rounded">cf-ipcountry</code> header on the redirect request.
                    {byCountry.length === 0 && ' Data populates for clicks from 2026-04-24 onward.'}
                  </p>
                  {byCountry.length === 0 ? (
                    <p className="text-gray-400 dark:text-gray-500 text-sm">No geo data yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart layout="vertical" data={byCountry.map(c => ({ name: c.name, clicks: c.clicks }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={DARK_TOOLTIP_STYLE} />
                            <Bar dataKey="clicks" fill="#F97316" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                              <th className="pb-2 pr-2">Country</th>
                              <th className="pb-2 text-right">Clicks</th>
                              <th className="pb-2 text-right">Share</th>
                            </tr>
                          </thead>
                          <tbody>
                            {byCountry.map(c => (
                              <tr key={c.code} className="border-b border-gray-100 dark:border-gray-700/50">
                                <td className="py-2 pr-2 text-gray-900 dark:text-white">
                                  <span className="mr-2">{c.flag}</span>
                                  {c.name}
                                  <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{c.code}</span>
                                </td>
                                <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{c.clicks}</td>
                                <td className="py-2 text-right text-gray-600 dark:text-gray-300">{c.share}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Hourly Activity Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button onClick={() => setHourlyOpen(!hourlyOpen)} className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-amber-100 dark:bg-amber-900 rounded">
                    <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Hourly Activity ({BROWSER_TZ})</span>
                </div>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${hourlyOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {hourlyOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Clicks by hour of day (your local timezone)</p>
                  {totalClicks === 0 ? (
                    <p className="text-gray-400 dark:text-gray-500 text-sm">No click data yet.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={270}>
                      <BarChart data={hourlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="#9CA3AF" interval={2} />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                        <Tooltip contentStyle={DARK_TOOLTIP_STYLE} />
                        <Bar dataKey="clicks" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Top URLs table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top 10 URLs by Clicks</h3>
        {topUrls.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm">No click data yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 pr-2 w-8">#</th>
                  <th className="pb-2 pr-2">Slug</th>
                  <th className="pb-2 pr-2">Target URL</th>
                  <th className="pb-2 text-right w-20">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {topUrls.map((item, i) => (
                  <tr
                    key={item.url.id}
                    onClick={() => navigate(`/service-shortlink/log?urlId=${item.url.id}`)}
                    className="border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                    title="Open in URL log"
                  >
                    <td className="py-2 pr-2 text-gray-400 dark:text-gray-500">{i + 1}</td>
                    <td className="py-2 pr-2 font-mono text-xs text-indigo-600 dark:text-indigo-400">/{item.url.slug}</td>
                    <td className="py-2 pr-2">
                      <div className="flex flex-col">
                        <span className="text-gray-700 dark:text-gray-300 text-xs truncate max-w-[200px]" title={item.url.original_url}>
                          {truncateUrl(item.url.original_url, 40)}
                        </span>
                        <div className="mt-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${(item.clicks / maxClicks) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 text-right font-semibold text-gray-900 dark:text-white">{item.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Activity Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {recentSearch.trim()
                ? `Showing ${filteredRecentClicks.length} of ${recentClicks.length} recent clicks`
                : 'Showing 20 most recent clicks'}
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={recentSearch}
              onChange={(e) => setRecentSearch(e.target.value)}
              placeholder="Filter by slug, device, browser, referrer…"
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
        {recentClicksRpc.loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : filteredRecentClicks.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            {recentSearch.trim() ? 'No rows match the filter.' : 'No click data yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 pr-4">Time</th>
                  <th className="pb-2 pr-4">Slug</th>
                  <th className="pb-2 pr-4">Device</th>
                  <th className="pb-2 pr-4">Browser</th>
                  <th className="pb-2">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecentClicks.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-2 pr-4 text-gray-600 dark:text-gray-300 whitespace-nowrap text-xs">
                      {formatTimestamp(c.timestamp)}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs text-indigo-600 dark:text-indigo-400">/{c.slug}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 text-xs capitalize">{c.device_type || '—'}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 text-xs">{c.client_name || '—'}</td>
                    <td className="py-2 text-gray-500 dark:text-gray-400 text-xs truncate max-w-[200px]" title={c.referrer || ''}>
                      {c.referrer ? truncateUrl(c.referrer, 30) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
